import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, CalendarClock, HandCoins, Users, Wallet } from 'lucide-react';
import {
  AGEING_BUCKETS,
  type InstallmentWorkItem,
  type OutstandingCustomer,
} from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader } from '@/components/ui/Card';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { PageHeader } from '@/components/ui/PageHeader';
import { StatCard } from '@/components/ui/StatCard';
import { useBranch } from '@/hooks/useBranch';
import { useCurrency } from '@/hooks/useCurrency';
import { apiErrorMessage } from '@/services/apiClient';
import {
  useAgeingQuery,
  useInstallmentsQuery,
  useOutstandingCustomersQuery,
} from '../api/receivables.api';

type Tab = 'OVERDUE' | 'UPCOMING' | 'CUSTOMERS';

const STATE_TONES: Record<string, 'neutral' | 'warning' | 'danger' | 'success' | 'brand'> = {
  UPCOMING: 'neutral',
  DUE: 'brand',
  PARTIALLY_PAID: 'warning',
  OVERDUE: 'danger',
  PAID: 'success',
  CANCELLED: 'neutral',
};

/**
 * What customers owe, and who to ring today.
 *
 * Ageing sits at the top because it is the question a manager asks first — how bad is it, and how
 * old. The worklists below answer the next one: which specific person, and about which instalment.
 * Overdue leads, because a list that opens on next month's money buries today's problem.
 */
export function ReceivablesPage() {
  const { t } = useTranslation();
  const { format } = useCurrency();
  const navigate = useNavigate();
  const { activeBranch } = useBranch();

  const [tab, setTab] = useState<Tab>('OVERDUE');
  const hasBranch = Boolean(activeBranch);

  const ageing = useAgeingQuery(hasBranch);
  const overdue = useInstallmentsQuery('OVERDUE', hasBranch);
  const upcoming = useInstallmentsQuery('UPCOMING', hasBranch && tab === 'UPCOMING');
  const customers = useOutstandingCustomersQuery(hasBranch && tab === 'CUSTOMERS');

  const installmentColumns: Column<InstallmentWorkItem>[] = [
    {
      key: 'customer',
      header: t('sales.fields.customer'),
      primary: true,
      render: (row) => (
        <span className="flex flex-col">
          <span className="text-ink font-medium">{row.customerName}</span>
          <span className="text-muted font-mono text-xs" dir="ltr">
            {row.orderReference} · {row.planReference}
          </span>
        </span>
      ),
    },
    {
      key: 'due',
      header: t('sales.receivables.fields.due'),
      render: (row) => (
        <span className="flex flex-col">
          <span className="text-ink" dir="ltr">
            {row.dueDate}
          </span>
          {row.daysOverdue ? (
            <span className="text-xs text-red-700">
              {t('sales.receivables.daysLate', { count: row.daysOverdue })}
            </span>
          ) : null}
        </span>
      ),
    },
    {
      key: 'instalment',
      header: t('sales.receivables.fields.instalment'),
      render: (row) => (
        <span className="text-muted text-xs">
          {t('sales.receivables.instalmentNumber', { number: row.sequence })}
        </span>
      ),
    },
    {
      key: 'outstanding',
      header: t('sales.receivables.fields.outstanding'),
      align: 'end',
      render: (row) => (
        <span className="text-ink font-medium" dir="ltr">
          {format(row.outstandingMinor)}
        </span>
      ),
    },
    {
      key: 'state',
      header: t('sales.fields.status'),
      render: (row) => (
        <Badge tone={STATE_TONES[row.state] ?? 'neutral'}>
          {t(`sales.receivables.states.${row.state}`)}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: t('common.actions'),
      align: 'end',
      render: (row) => (
        <Button
          variant="ghost"
          size="sm"
          aria-label={t('sales.receivables.openPlanFor', { customer: row.customerName })}
          onClick={() => navigate(`/sales/plans/${row.planId}`)}
        >
          {t('sales.receivables.openPlan')}
        </Button>
      ),
    },
  ];

  const customerColumns: Column<OutstandingCustomer>[] = [
    {
      key: 'customer',
      header: t('sales.fields.customer'),
      primary: true,
      render: (row) => <span className="text-ink font-medium">{row.customerName}</span>,
    },
    {
      key: 'outstanding',
      header: t('sales.receivables.fields.outstanding'),
      align: 'end',
      render: (row) => (
        <span className="text-ink font-medium" dir="ltr">
          {format(row.outstandingMinor)}
        </span>
      ),
    },
    {
      key: 'overdue',
      header: t('sales.receivables.fields.overdue'),
      align: 'end',
      render: (row) =>
        row.overdueMinor > 0 ? (
          <span className="font-semibold text-red-700" dir="ltr">
            {format(row.overdueMinor)}
          </span>
        ) : (
          <span className="text-muted text-xs">{t('sales.receivables.nothingLate')}</span>
        ),
    },
    {
      key: 'available',
      header: t('sales.receivables.fields.available'),
      align: 'end',
      render: (row) =>
        row.availableCreditMinor === null ? (
          // Some of this customer's debt is in a branch the caller cannot reach, so no honest headroom exists here.
          <span className="text-muted text-xs">
            {t('sales.receivables.availableUnavailable')}
            {' · '}
            <span dir="ltr">{format(row.creditLimitMinor)}</span>
          </span>
        ) : (
          <span className="text-muted text-xs" dir="ltr">
            {format(row.availableCreditMinor)} / {format(row.creditLimitMinor)}
          </span>
        ),
    },
    {
      key: 'actions',
      header: t('common.actions'),
      align: 'end',
      render: (row) => (
        <Button
          variant="ghost"
          size="sm"
          aria-label={t('sales.receivables.openCustomerFor', { customer: row.customerName })}
          onClick={() => navigate(`/customers/${row.customerId}`)}
        >
          {t('sales.receivables.openCustomer')}
        </Button>
      ),
    },
  ];

  const totals = ageing.data?.totals;
  const overdueTotal = totals ? totals.D1_30 + totals.D31_60 + totals.D61_90 + totals.D90_PLUS : 0;

  return (
    <div className="space-y-6">
      <PageHeader title={t('sales.receivables.title')} subtitle={t('sales.receivables.subtitle')} />

      {!hasBranch ? <Alert tone="warning">{t('sales.receivables.selectBranch')}</Alert> : null}

      {hasBranch ? (
        <>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            <StatCard
              icon={Wallet}
              label={t('sales.receivables.stats.outstanding')}
              value={format(ageing.data?.totalMinor ?? 0)}
              hint={t('sales.receivables.stats.outstandingHint')}
              isLoading={ageing.isPending}
            />
            <StatCard
              icon={AlertTriangle}
              label={t('sales.receivables.stats.overdue')}
              value={format(overdueTotal)}
              hint={t('sales.receivables.stats.overdueHint')}
              tone="danger"
              isLoading={ageing.isPending}
            />
            <StatCard
              icon={CalendarClock}
              label={t('sales.receivables.stats.current')}
              value={format(totals?.CURRENT ?? 0)}
              hint={t('sales.receivables.stats.currentHint')}
              tone="success"
              isLoading={ageing.isPending}
            />
          </div>

          <Card>
            <CardHeader
              title={t('sales.receivables.ageingTitle')}
              subtitle={
                ageing.data
                  ? t('sales.receivables.asOf', { date: ageing.data.asOf })
                  : t('sales.receivables.ageingHint')
              }
            />
            <div className="grid gap-px bg-line sm:grid-cols-5">
              {AGEING_BUCKETS.map((bucket) => (
                <div key={bucket} className="bg-surface flex flex-col gap-1 p-4">
                  <span className="text-muted text-xs font-medium tracking-wide uppercase">
                    {t(`sales.receivables.buckets.${bucket}`)}
                  </span>
                  <span
                    className={
                      bucket === 'D90_PLUS' && (totals?.[bucket] ?? 0) > 0
                        ? 'font-semibold text-red-700'
                        : 'text-ink font-semibold'
                    }
                    dir="ltr"
                  >
                    {format(totals?.[bucket] ?? 0)}
                  </span>
                </div>
              ))}
            </div>
          </Card>

          <Card>
            <CardHeader
              title={t('sales.receivables.worklistTitle')}
              action={
                <div className="flex flex-wrap gap-2">
                  <TabButton
                    icon={AlertTriangle}
                    label={t('sales.receivables.tabs.overdue')}
                    count={overdue.data?.length}
                    isActive={tab === 'OVERDUE'}
                    onClick={() => setTab('OVERDUE')}
                  />
                  <TabButton
                    icon={CalendarClock}
                    label={t('sales.receivables.tabs.upcoming')}
                    count={upcoming.data?.length}
                    isActive={tab === 'UPCOMING'}
                    onClick={() => setTab('UPCOMING')}
                  />
                  <TabButton
                    icon={Users}
                    label={t('sales.receivables.tabs.customers')}
                    count={customers.data?.length}
                    isActive={tab === 'CUSTOMERS'}
                    onClick={() => setTab('CUSTOMERS')}
                  />
                </div>
              }
            />

            {tab === 'CUSTOMERS' ? (
              <DataTable
                caption={t('sales.receivables.customersCaption')}
                columns={customerColumns}
                rows={customers.data}
                rowKey={(row) => row.customerId}
                isLoading={customers.isPending}
                isError={customers.isError}
                errorMessage={
                  apiErrorMessage(customers.error) ?? t('sales.receivables.errors.load')
                }
                onRetry={() => void customers.refetch()}
                emptyIcon={HandCoins}
                emptyTitle={t('sales.receivables.noCustomers')}
                emptyDescription={t('sales.receivables.noCustomersHint')}
              />
            ) : (
              <DataTable
                caption={t('sales.receivables.worklistCaption')}
                columns={installmentColumns}
                rows={tab === 'OVERDUE' ? overdue.data : upcoming.data}
                rowKey={(row) => row.id}
                isLoading={tab === 'OVERDUE' ? overdue.isPending : upcoming.isPending}
                isError={tab === 'OVERDUE' ? overdue.isError : upcoming.isError}
                errorMessage={t('sales.receivables.errors.load')}
                onRetry={() => void (tab === 'OVERDUE' ? overdue.refetch() : upcoming.refetch())}
                emptyIcon={HandCoins}
                emptyTitle={
                  tab === 'OVERDUE'
                    ? t('sales.receivables.nothingOverdue')
                    : t('sales.receivables.nothingUpcoming')
                }
                emptyDescription={
                  tab === 'OVERDUE'
                    ? t('sales.receivables.nothingOverdueHint')
                    : t('sales.receivables.nothingUpcomingHint')
                }
              />
            )}
          </Card>
        </>
      ) : null}
    </div>
  );
}

function TabButton({
  icon: Icon,
  label,
  count,
  isActive,
  onClick,
}: {
  icon: typeof AlertTriangle;
  label: string;
  count: number | undefined;
  isActive: boolean;
  onClick: () => void;
}) {
  return (
    <Button
      variant={isActive ? 'primary' : 'secondary'}
      size="sm"
      aria-pressed={isActive}
      onClick={onClick}
    >
      <Icon className="size-4" aria-hidden />
      {label}
      {count !== undefined ? <span className="ms-1 opacity-80">{count}</span> : null}
    </Button>
  );
}
